import React, { useEffect, useState } from 'react';
import { aiAgentAPI, settingsAPI } from '../../lib/api';

export default function SettingsPage() {
  const [aiRules, setAiRules] = useState('');
  const [globalAI, setGlobalAI] = useState(true);
  const [balance, setBalance] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);
  const [rulesSuccess, setRulesSuccess] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [rulesRes, globalRes, balanceRes] = await Promise.all([
        settingsAPI.getAIRules().catch(() => null),
        aiAgentAPI.getGlobalStatus().catch(() => null),
        settingsAPI.getBalance().catch(() => null)
      ]);
      setAiRules(rulesRes?.data?.data?.aiRules || '');
      setGlobalAI(globalRes?.data?.globalAIEnabled ?? globalRes?.data?.aiEnabled ?? true);
      const bal = balanceRes?.data?.data?.data?.balances?.openaiBalance ?? balanceRes?.data?.balances?.openaiBalance ?? null;
      setBalance(bal);
    } catch (_err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveRules = async () => {
    setSaving(true);
    setRulesSuccess(false);
    try {
      await settingsAPI.updateAIRules(aiRules);
      setRulesSuccess(true);
      setTimeout(() => setRulesSuccess(false), 2000);
    } catch (_err) {
      setError('فشل حفظ القواعد');
    } finally {
      setSaving(false);
    }
  };

  const toggleGlobalAI = async () => {
    const next = !globalAI;
    try {
      await aiAgentAPI.toggleGlobal(next);
      setGlobalAI(next);
    } catch (_err) {
      setError('فشل تبديل الذكاء الاصطناعي');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdSuccess(false);
    try {
      await settingsAPI.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setPwdSuccess(true);
      setTimeout(() => setPwdSuccess(false), 2000);
    } catch (_err) {
      setError(_err.response?.data?.message || 'فشل تحديث كلمة المرور');
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 flex items-center justify-center">
        <p className="text-dark_text_2">جاري تحميل الإعدادات...</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full dark:bg-dark_bg_4 select-none border-s dark:border-s-dark_border_2 border-b-[6px] border-b-green_2 overflow-auto">
      <div className="p-4 sm:p-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg sm:text-[22px] font-semibold text-dark_text_1">الإعدادات</h2>
            <p className="text-sm text-dark_text_2 mt-1">إعدادات الذكاء الاصطناعي والأمان</p>
          </div>
          <button onClick={load} className="px-3 py-1.5 rounded-lg bg-dark_bg_3 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1 text-sm font-medium">
            تحديث
          </button>
        </div>

        {error && (
          <div className="p-3 rounded-lg bg-red-900/30 border border-red-700 text-red-300 text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">حالة الذكاء الاصطناعي</p>
            <p className="mt-2 text-2xl text-dark_text_1 font-semibold">{globalAI ? 'مفعّل' : 'معطّل'}</p>
            <button
              onClick={toggleGlobalAI}
              className={`mt-2 px-3 py-1 rounded-lg text-sm font-medium ${globalAI ? 'bg-green_3 text-white' : 'bg-dark_bg_4 text-dark_text_2 border border-dark_border_2'}`}
            >
              {globalAI ? 'نشط' : 'غير نشط'}
            </button>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">رصيد OpenAI</p>
            <p className="mt-2 text-2xl text-emerald-300 font-semibold">
              {balance === null ? 'N/A' : `$${Number(balance).toFixed(2)}`}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-xs uppercase tracking-wide text-dark_text_2">طول قواعد الذكاء الاصطناعي</p>
            <p className="mt-2 text-2xl text-dark_text_1 font-semibold">{(aiRules || '').length} chars</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-dark_text_1">تفعيل الذكاء الاصطناعي العام</h3>
              <button
                onClick={toggleGlobalAI}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${globalAI ? 'bg-green_1 hover:bg-green_2 text-white' : 'bg-dark_bg_4 hover:bg-dark_hover_1 border border-dark_border_2 text-dark_text_1'}`}
              >
                {globalAI ? 'مفعّل' : 'معطّل'}
              </button>
            </div>
            <p className="text-sm text-dark_text_2">
              {globalAI ? 'الذكاء الاصطناعي نشط لجميع الجلسات المدعومة.' : 'الذكاء الاصطناعي معطّل عالمياً.'}
            </p>
            <p className="text-sm text-dark_text_2">رصيد OpenAI: {balance === null ? 'غير متوفر' : `$${Number(balance).toFixed(2)}`}</p>
          </div>

          <form onSubmit={changePassword} className="card p-4 space-y-3">
            <h3 className="font-medium text-dark_text_1">تغيير كلمة المرور</h3>
            <input
              className="input py-1.5"
              type="password"
              placeholder="كلمة المرور الحالية"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <input
              className="input py-1.5"
              type="password"
              placeholder="كلمة المرور الجديدة"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <div className="flex gap-2">
              <button type="submit" className="px-3 py-1.5 rounded-lg bg-green_1 hover:bg-green_2 text-white text-sm font-medium">
                تحديث كلمة المرور
              </button>
              {pwdSuccess && <span className="text-emerald-400 text-sm self-center">تم الحفظ</span>}
            </div>
          </form>
        </div>

        <div className="card p-4 space-y-4">
          <div>
            <h3 className="font-medium text-dark_text_1">قواعد الذكاء الاصطناعي الحرجة</h3>
            <p className="text-sm text-dark_text_2 mt-0.5">ضبط قواعد حرجة للذكاء الاصطناعي تتجاوز التعليمات الافتراضية</p>
          </div>

          <div className="p-4 rounded-lg bg-blue-900/30 border border-blue-700/50">
            <p className="text-sm font-medium text-blue-200 mb-2">كيفية استخدام قواعد الذكاء الاصطناعي:</p>
            <ul className="list-disc list-inside space-y-1.5 text-sm text-blue-100/90">
              <li>القواعد المضافة هنا لها أولوية عالية وتتجاوز التعليمات الافتراضية</li>
              <li>استخدمها لسياسات العمل أو قواعد محددة تريد أن يتبعها الذكاء الاصطناعي</li>
              <li>مثال: &quot;استخدم دائماً اللهجة الفلسطينية&quot; أو &quot;لا تعرض أسعار المنتجات مباشرة&quot;</li>
              <li>يمكنك إضافة عدة قواعد، كل واحدة في سطر أو فقرة منفصلة</li>
              <li>تطبق القواعد هنا على جميع الجلسات والتفاعلات</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-dark_text_2 mb-1">قواعد الذكاء الاصطناعي (اختياري)</label>
            <textarea
              className="input min-h-[200px] py-1.5"
              value={aiRules}
              onChange={(e) => setAiRules(e.target.value)}
              placeholder="أدخل قواعد الذكاء الاصطناعي الحرجة هنا…"
              disabled={saving}
            />
            <p className="text-xs text-dark_text_2 mt-2">
              القواعد المضافة هنا تتجاوز التعليمات الافتراضية ولها أولوية عالية جداً
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={saveRules}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg bg-green_1 hover:bg-green_2 text-white text-sm font-medium disabled:opacity-60"
            >
              {saving ? 'جاري الحفظ…' : 'حفظ القواعد'}
            </button>
            {rulesSuccess && <span className="text-emerald-400 text-sm self-center">تم الحفظ</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
